import React, { useMemo, useState } from 'react';
import { Button, OverlayTrigger, Tooltip, Badge, Popover } from 'react-bootstrap';
import { FaSmile } from 'react-icons/fa';
import { messagingService } from '../services/messagingService';
import './MessageReactions.css';
import safeStorage from '../../../utils/safeStorage';

// Quick emoji options
const QUICK_EMOJIS = [
  { emoji: '👍', name: 'thumbs up' },
  { emoji: '👎', name: 'thumbs down' },
  { emoji: '😂', name: 'laugh' },
  { emoji: '😊', name: 'smiling face' },
  { emoji: '🦌', name: 'deer' },
  { emoji: '🌽', name: 'corn' }
];

export function MessageReactions({ message, onReactionUpdate }) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reactionsOverride, setReactionsOverride] = useState(null);

  const applyLocalToggle = (emoji) => {
    // Build reactions from override if present, else from message
    const base = Array.isArray(reactionsOverride) ? reactionsOverride : (message.reactions || []);
    const token = safeStorage.getItem('token');
    let currentUser = null;
    try {
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        currentUser = { id: payload.user_id, username: payload.username || 'Unknown' };
      }
    } catch (_) {}
    if (!currentUser) return; // cannot apply without user context
    const next = base.map(r => ({ ...r, users: (r.users || []).map(u => ({ ...u })) }));
    const idx = next.findIndex(g => g.emoji === emoji);
    if (idx === -1) {
      next.push({ emoji, users: [currentUser], count: 1 });
    } else {
      const hasUser = (next[idx].users || []).some(u => Number(u.id) === Number(currentUser.id));
      if (hasUser) {
        const filtered = next[idx].users.filter(u => Number(u.id) !== Number(currentUser.id));
        if (filtered.length === 0) {
          next.splice(idx, 1);
        } else {
          next[idx] = { ...next[idx], users: filtered, count: filtered.length };
        }
      } else {
        const users = [...(next[idx].users || []), currentUser];
        next[idx] = { ...next[idx], users, count: users.length };
      }
    }
    setReactionsOverride(next);
  };

  const handleQuickEmojiClick = async (emoji) => {
    try {
      setLoading(true);
      await messagingService.toggleReaction(message.id, emoji);
      applyLocalToggle(emoji);
      if (onReactionUpdate) {
        onReactionUpdate();
      }
      
      setShowEmojiPicker(false);
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReactionClick = async (emoji) => {
    try {
      setLoading(true);
      await messagingService.toggleReaction(message.id, emoji);
      applyLocalToggle(emoji);
      if (onReactionUpdate) {
        onReactionUpdate();
      }
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
    } finally {
      setLoading(false);
    }
  };



  const renderedReactions = useMemo(() => (Array.isArray(reactionsOverride) ? reactionsOverride : (message.reactions || [])), [reactionsOverride, message.reactions]);

  const getCurrentUserReactions = () => {
    // Get current user from JWT token
    let currentUser = null;
    try {
      const token = safeStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        currentUser = {
          id: payload.user_id,
          username: payload.username || 'Unknown'
        };
      }
    } catch (error) {
      console.error('Error parsing JWT token:', error);
    }
    
    const userReactions = new Set();
    
    if (currentUser) {
      renderedReactions.forEach(reactionGroup => {
        reactionGroup.users.forEach(user => {
          if (user.id === currentUser.id) {
            userReactions.add(reactionGroup.emoji);
          }
        });
      });
    }
    
    return userReactions;
  };

  const currentUserReactions = getCurrentUserReactions();



  return (
    <div className="message-reactions">
      {/* Existing Reactions */}
      {renderedReactions && renderedReactions.length > 0 && (
        <div className="reactions-display">
          {renderedReactions.map((reactionGroup, index) => (
            <OverlayTrigger
              key={`${reactionGroup.emoji}-${index}`}
              placement="top"
              overlay={
                <Tooltip>
                  <div>
                    {reactionGroup.users.map(user => user.username).join(', ')}
                  </div>
                </Tooltip>
              }
            >
              <Button
                variant={currentUserReactions.has(reactionGroup.emoji) ? "primary" : "outline-secondary"}
                size="sm"
                className="reaction-button"
                onClick={() => handleReactionClick(reactionGroup.emoji)}
                disabled={loading}
              >
                <span className="reaction-emoji">{reactionGroup.emoji}</span>
                <Badge bg="secondary" className="reaction-count">
                  {reactionGroup.count}
                </Badge>
              </Button>
            </OverlayTrigger>
          ))}
        </div>
      )}

      {/* Single Add Reaction Button with Popover */}
      <OverlayTrigger
        trigger="click"
        placement="top"
        show={showEmojiPicker}
        onToggle={setShowEmojiPicker}
        container={typeof document !== 'undefined' ? document.body : undefined}
        popperConfig={{ strategy: 'fixed' }}
        overlay={
          <Popover id="emoji-picker-popover" className="emoji-picker-popover">
            <Popover.Body className="p-2">
              <div className="quick-emoji-row">
                {QUICK_EMOJIS.map((quickEmoji, index) => (
                  <Button
                    key={index}
                    variant="outline-secondary"
                    size="sm"
                    className="quick-emoji-popover-button"
                    onClick={() => handleQuickEmojiClick(quickEmoji.emoji)}
                    disabled={loading}
                    title={quickEmoji.name}
                  >
                    {quickEmoji.emoji}
                  </Button>
                ))}
              </div>
            </Popover.Body>
          </Popover>
        }
        rootClose
      >
        <Button
          variant="outline-secondary"
          size="sm"
          className="add-reaction-button"
          disabled={loading}
        >
          <FaSmile />
        </Button>
      </OverlayTrigger>
    </div>
  );
}
